import React, { useState, useEffect } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { Formik, Field, ErrorMessage } from "formik";
import toast from "react-hot-toast";
import OtpInput from "react-otp-input";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import {
  useSendOtpMutation,
  useVerifyOtpMutation,
} from "store/services/user.service";

const ChangeEmail = ({ onCancel, refetch }) => {
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [sendOtp] = useSendOtpMutation();
  const [verifyOtp] = useVerifyOtpMutation();
  const [attempts, setAttempts] = useState(0);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [timer, setTimer] = useState(15);
  const navigate = useNavigate();

  const handleSendOtp = async (values) => {
    try {
      await sendOtp({ email: values?.email }).unwrap();
      setOtpSent(true);
      toast.success("OTP sent to your new email address");
      startTimer();
    } catch (error) {
      console.error("Error sending OTP", error);
      toast.error(error?.data?.message);
    }
  };

  const handleVerifyOtp = async (values) => {
    try {
      await verifyOtp({ otp, email: values.email }).unwrap();
      toast.success("Email changed successfully");
      refetch()
      onCancel()
    } catch (error) {
      console.error("Error verifying OTP", error);
      toast.error(error?.data?.message);
    }
  };

  const startTimer = () => {
    setIsTimerActive(true);
    setTimer(15);
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setIsTimerActive(false);
          return 15;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResendOtp = async (values) => {
    if (attempts < 2) {
      setAttempts(attempts + 1);
      await handleSendOtp(values);
    } else {
      toast.error("Maximum resend attempts reached. Please try again later.");
      navigate("/my-profile");
    }
  };

  useEffect(() => {
    if (timer <= 0) {
      setIsTimerActive(false);
    }
  }, [timer]);

  return (
    <div className="change-email-section">
      <Formik
        initialValues={{
          email: "",
          otp: "",
        }}
        validationSchema={Yup.object().shape({
          email: Yup.string()
            .email("Invalid email format")
            .required("Email is required"),
          otp: otpSent
            ? Yup.string()
                .length(4, "OTP must be 4 digits")
                .required("OTP is required")
            : Yup.string(),
        })}
        onSubmit={(values) => {
          if (!otpSent) {
            handleSendOtp(values);
          } else {
            handleVerifyOtp(values);
          }
        }}
      >
        {({ handleSubmit, values }) => (
          <Form onSubmit={handleSubmit}>
            <Row>
              {!otpSent ? (
                <>
                  <Col lg="12" className="my-3">
                    <div className="position-relative labelInput">
                      <Field
                        type="email"
                        name="email"
                        className="form-control"
                        placeholder="New Email"
                      />
                      <label
                        htmlFor="email"
                        className="form-label position-absolute d-inline-block bg-white m-0 px-1"
                      >
                        New Email
                      </label>
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                  </Col>
                  <Col lg="7" className="my-2">
                    <Button
                      type="submit"
                      className="d-inline-flex align-items-center justify-content-center commonBtn w-100"
                    >
                      Send OTP
                    </Button>
                  </Col>
                </>
              ) : (
                <>
                  <Col lg="12" className="my-3">
                    <div className="FormHeading">
                      <h4 className="m-0 fw-sbold themeClr">
                        Verification Code
                      </h4>
                      <p className="m-0">
                        Please type the verification code sent to{" "}
                        <span className="themeClr">{values?.email}</span>
                      </p>
                    </div>
                  </Col>
                  <Col lg="12" className="my-3">
                    <div className="otp mb-3">
                      <OtpInput
                        value={otp}
                        onChange={setOtp}
                        numInputs={4}
                        renderInput={(props) => <input {...props} />}
                      />
                    </div>
                    <p className="text-muted fw-sbold m-0">
                      I don’t receive a code!{" "}
                      {isTimerActive ? (
                        <span className="themeClr fw-sbold">
                          Please wait {timer}s to resend
                        </span>
                      ) : (
                        <span
                          className="themeClr fw-sbold"
                          role="button"
                          onClick={() => handleResendOtp(values)}
                        >
                          Resend OTP
                        </span>
                      )}
                    </p>
                  </Col>
                  <Col lg="7" className="my-3">
                    <Button
                      type="submit"
                      onClick={() => handleVerifyOtp(values)}
                      className="d-inline-flex align-items-center justify-content-center commonBtn cancelBtn w-100"
                    >
                      VERIFY OTP
                    </Button>
                  </Col>
                </>
              )}
              <Col lg="7" className="my-2">
                <Button
                  type="button"
                  className="d-inline-flex align-items-center justify-content-center commonBtn cancelBtn w-100"
                  onClick={onCancel}
                >
                  Cancel
                </Button>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default ChangeEmail;
